import { useEffect, useRef } from 'react';
import { DEFAULT_LOCALE, intl } from '@frameio/i18n';
import {
  useIsSegmentLoaded,
  useIsUserIdentified,
} from '@frameio/segment/contexts';
import { enqueueOrRun } from '@frameio/segment-ot';
import type { LanguageUpdatedSource } from './types';

const LANGUAGE_UPDATED_SOURCE: LanguageUpdatedSource = 'Browser';

const SEGMENT_TRAITS_STORAGE_KEY = 'ajs_user_traits';

function normalizeLocale(locale: string): string {
  return locale.replace(/_/g, '-').toLowerCase();
}

function getCachedLanguageTrait(): string | undefined {
  try {
    /* eslint-disable no-storage/no-browser-storage */
    const raw = window.localStorage.getItem(SEGMENT_TRAITS_STORAGE_KEY);
    /* eslint-enable no-storage/no-browser-storage */
    if (!raw) {
      return undefined;
    }
    const { language } = JSON.parse(raw) as { language?: string };
    return typeof language === 'string' ? language : undefined;
  } catch {
    return undefined;
  }
}

function shouldTrackLanguageUpdate(
  previousLocale: string | undefined,
  currentLocale: string
): boolean {
  const normalizedCurrent = normalizeLocale(currentLocale);

  if (previousLocale) {
    return normalizeLocale(previousLocale) !== normalizedCurrent;
  }

  return normalizedCurrent !== normalizeLocale(DEFAULT_LOCALE);
}

export function useTrackLanguagePreferenceIfCachedTraitChanged(
  authenticatedUserId: string | null | undefined
): void {
  const locale = intl().locale;
  const { isSegmentLoaded } = useIsSegmentLoaded();
  const { isUserIdentified } = useIsUserIdentified();
  const hasChecked = useRef(false);

  useEffect(
    function trackServedLocaleOnRevisit() {
      if (
        isUserIdentified ||
        !isSegmentLoaded ||
        !authenticatedUserId ||
        hasChecked.current
      ) {
        return;
      }

      hasChecked.current = true;

      const previousLocale = getCachedLanguageTrait();
      if (!shouldTrackLanguageUpdate(previousLocale, locale)) {
        return;
      }

      enqueueOrRun({
        enqueueEvent: true,
        event: () => {
          window.analytics?.track('language-updated', {
            language: locale,
            source: LANGUAGE_UPDATED_SOURCE,
          });
        },
      });
    },
    [authenticatedUserId, isSegmentLoaded, isUserIdentified, locale]
  );
}
